import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiArrowLeft, FiPlus, FiSearch, FiEdit, FiTrash2, FiUser, FiMail, FiShield, FiX, FiBriefcase, FiLock, FiChevronDown, FiGlobe } = FiIcons;

const InternalStaffManagement = () => {
  const navigate = useNavigate();
  const { users, departments = [], deleteUser, addUser, updateUser } = useData();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // NY GENERERING: INT-XXXX (4 tall)
  const generateShortId = () => `INT-${Math.floor(1000 + Math.random() * 9000)}`;

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'seller',
    department_id: '',
    id: generateShortId()
  });

  const internalRoles = ['superadmin', 'dept_leader', 'sales_chief', 'team_leader', 'seller'];
  const internalUsers = (users || [])
    .filter(u => internalRoles.includes(u.role) || u.company_id === 'hms_internal')
    .filter(u => u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || u.email?.toLowerCase().includes(searchTerm.toLowerCase()));

  const roleOptions = [
    { value: 'seller', label: 'Selger' },
    { value: 'dept_leader', label: 'Avdelingsleder (Landssjef)' },
    { value: 'sales_chief', label: 'Salgssjef' },
    { value: 'team_leader', label: 'Teamleder' },
    { value: 'superadmin', label: 'Global Admin' }
  ];

  const handleOpenEdit = (user) => {
    setSelectedUserId(user.id);
    setFormData({
      name: user.name,
      email: user.email,
      password: user.password,
      role: user.role,
      department_id: user.department_id || user.departmentId || '',
      id: user.id
    });
    setIsEditing(true);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'seller',
      department_id: '',
      id: generateShortId()
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const payload = {
      ...formData,
      company_id: 'hms_internal',
      sellerId: formData.id // Brukes for referral-linker
    };

    const result = isEditing ? await updateUser(selectedUserId, payload) : await addUser(payload);
    
    if (result.success) {
      handleCloseModal();
    } else {
      alert("Feil: " + result.error);
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <header className="bg-white shadow-sm border-b h-16 flex items-center justify-between px-8 sticky top-0 z-20">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <h1 className="text-lg font-bold text-gray-900">Interne Ansatte</h1>
        </div>
        <button onClick={() => { setIsEditing(false); setShowModal(true); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2">
          <SafeIcon icon={FiPlus} /> Ny Ansatt
        </button>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <table className="w-full text-left">
            <thead className="bg-gray-50 text-xs font-bold text-gray-500 uppercase">
              <tr>
                <th className="px-6 py-4">Bruker</th>
                <th className="px-6 py-4">Rolle</th>
                <th className="px-6 py-4">ID (Ref. kode)</th>
                <th className="px-6 py-4">Tilknytning</th>
                <th className="px-6 py-4 text-right">Handling</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {internalUsers.map(u => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold">
                        {u.name.charAt(0)}
                      </div>
                      <div>
                        <div className="font-bold text-gray-900">{u.name}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="bg-gray-100 px-2 py-1 rounded text-[10px] font-black uppercase">
                      {roleOptions.find(r => r.value === u.role)?.label || u.role}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <code className="text-xs font-mono bg-gray-50 px-2 py-1 rounded border">{u.id}</code>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <SafeIcon icon={FiGlobe} className="text-gray-400" />
                      {departments.find(d => d.id === (u.department_id || u.departmentId))?.name || 'Global'}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => handleOpenEdit(u)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                        <SafeIcon icon={FiEdit} />
                      </button>
                      <button onClick={() => deleteUser(u.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                        <SafeIcon icon={FiTrash2} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
              <div className="p-6 border-b flex justify-between items-center bg-gray-50">
                <h3 className="font-bold text-gray-900">{isEditing ? 'Rediger Ansatt' : 'Opprett Ny Ansatt'}</h3>
                <button onClick={handleCloseModal}><SafeIcon icon={FiX} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-8 space-y-4">
                <div>
                  <label className="text-[10px] font-black text-gray-400 mb-1 block uppercase">ID / Ref.kode</label>
                  <input type="text" readOnly={isEditing} value={formData.id} onChange={e => setFormData({...formData, id: e.target.value})} className="w-full p-3 bg-gray-50 border rounded-xl font-mono" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 mb-1 block uppercase">Navn</label>
                  <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full p-3 border rounded-xl" />
                </div>
                <div>
                  <label className="text-[10px] font-black text-gray-400 mb-1 block uppercase">E-post</label>
                  <input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full p-3 border rounded-xl" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-gray-400 mb-1 block uppercase">Rolle</label>
                    <select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} className="w-full p-3 border rounded-xl bg-white">
                      {roleOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-gray-400 mb-1 block uppercase">Land</label>
                    <select value={formData.department_id} onChange={e => setFormData({...formData, department_id: e.target.value})} className="w-full p-3 border rounded-xl bg-white">
                      <option value="">Global</option>
                      {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                </div>
                <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black shadow-lg hover:bg-blue-700 transition-all mt-4">
                  {isSubmitting ? 'Lagrer...' : (isEditing ? 'Oppdater ansatt' : 'Opprett ansatt')}
                </button>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InternalStaffManagement;